import { useQuery } from '@tanstack/react-query';
import type { LoginAttempt, LoginOutcome } from '@mys/contracts';
import { api } from './api.js';

/* ============================================================================
   Login history.
   ----------------------------------------------------------------------------
   The scope is NOT a parameter here, and that is deliberate. The API decides
   from the session whether this is one account's history or everybody's, and
   sends back which it chose. A `?adminId=` the browser could set would be a
   staff user reading when the owner last logged in and from where.
   ========================================================================== */

export interface LoginHistory {
  attempts: LoginAttempt[];
  scope: 'self' | 'all';
}

export const OUTCOME_LABELS: Record<LoginOutcome, string> = {
  success: 'Signed in',
  bad_password: 'Wrong password',
  no_account: 'No such account',
  disabled: 'Account disabled',
  locked: 'Blocked — account was locked',
  bad_totp: 'Wrong two-factor code',
  recovery_used: 'Signed in with a recovery code',
};

/**
 * Which existing pill each outcome wears.
 *
 * THREE VARIANTS, AND NO FOURTH. `.pill`, `.pill--warn` and `.pill--muted` are
 * every pill app.css has, and each one's colour combination is already a row
 * in `apps/web/scripts/contrast-audit.mjs`. A new variant here would be a new
 * combination that the audit does not know about and will not discover, which
 * is how the FAQ's hover sat below AA for weeks. CLAUDE.md.
 */
export const OUTCOME_PILL: Record<LoginOutcome, string> = {
  success: 'pill',
  bad_password: 'pill pill--warn',
  no_account: 'pill pill--warn',
  /* Muted rather than warn: a disabled account being refused is the system
     working, not an alarm. */
  disabled: 'pill pill--muted',
  locked: 'pill pill--warn',
  bad_totp: 'pill pill--warn',
  /* Warn, not the plain pill, even though this IS a successful sign-in. A
     recovery code means a phone is gone — or somebody else has the printed
     codes. It is the one row on this screen worth looking at twice, and
     dressing it as an ordinary success is how it gets scrolled past. */
  recovery_used: 'pill pill--warn',
};

/**
 * Turn a user-agent string into something a person can read.
 *
 * Deliberately rough. A user agent is self-reported and half of it is
 * historical fiction — every browser still claims to be Mozilla — so this
 * answers "was that me, on my phone?" and makes no attempt at more. The raw
 * string stays available in the row's title attribute for when it matters.
 */
export function describeAgent(agent: string | null): string {
  if (!agent) return 'Unknown device';

  const browser = /Edg\//.test(agent)
    ? 'Edge'
    : /OPR\/|Opera/.test(agent)
      ? 'Opera'
      : /Firefox\//.test(agent)
        ? 'Firefox'
        : /* Chrome must be tested before Safari: every Chrome user agent also
         contains "Safari", and the other way round it never matches. */
          /Chrome\//.test(agent)
          ? 'Chrome'
          : /Safari\//.test(agent)
            ? 'Safari'
            : 'Browser';

  const platform = /Android/.test(agent)
    ? 'Android'
    : /iPhone|iPad|iPod/.test(agent)
      ? 'iOS'
      : /Windows/.test(agent)
        ? 'Windows'
        : /Mac OS X/.test(agent)
          ? 'macOS'
          : /Linux/.test(agent)
            ? 'Linux'
            : '';

  return platform ? `${browser} on ${platform}` : browser;
}

export function useLoginHistory() {
  return useQuery({
    queryKey: ['login-history'],
    queryFn: () => api.get<LoginHistory>('/api/auth/login-history'),
  });
}
